import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { authApi, LoginRequest } from '@/apis/auth';
import { usersApi } from '@/apis/users';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/authStore';
import { markSession } from '@/utils/authGuard';

export const useLogin = () => {
  const { setUser, logout } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: async () => {
      // Both tokens are httpOnly cookies the backend just set directly — the
      // browser JS never sees either of them. This flag is only a hint for
      // route guards; it carries no auth power of its own.
      markSession();

      const user = await usersApi.getProfile();

      // Only admin and staff (includes salespersons) may access the portal
      if (user.userRole !== 'admin' && user.userRole !== 'staff') {
        logout();
        toast.warning('Access denied. You do not have permission to access this portal.');
        return;
      }

      setUser(user);
      toast.success('Welcome back!');
      navigate({ to: ROUTES.ORDER_MANAGEMENT, search: { page: 1, limit: undefined, search: undefined, status: undefined, tab: undefined, view: undefined } });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Login failed');
    },
  });
};

export const useLogout = () => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: async () => {
      logout();
      queryClient.clear();
      toast.success('Logged out successfully');
      navigate({ to: '/login' });
    },
    onError: () => {
      // Force logout even if API call fails
      logout();
      queryClient.clear();
      navigate({ to: '/login' });
    },
  });
};
